import { Box, Stack } from "@mui/material";
import { ReactNode, useEffect, useRef } from "react";
import { useOutsideClick } from "../../../hooks/useOutSideClick";
import { CustomInput } from "../CustomInput";
import { SearchIcon } from "../../images/SearchIcon";
import { CustomButton } from "../CustomButton";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  search: string;
  setSearch: (search: string) => void;
  selectedCount: number;
  placeholder: string;
  children: ReactNode;
  closeHandler: () => void;
  submitHandler: () => void;
  cancelHandler: () => void;
  fullwidth?: boolean;
  showCounter?: boolean;
  listMinHeight?: string;
  listMaxHeight?: string;
};

export const DropdownInput = ({
  isOpen,
  setIsOpen,
  search,
  setSearch,
  placeholder,
  selectedCount,
  children,
  submitHandler,
  cancelHandler,
  closeHandler,
  showCounter,
  fullwidth,
  listMinHeight,
  listMaxHeight = "254px",
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    elementRef: dropdownRef,
    onOutsideClick: closeHandler,
    isOpened: isOpen,
    wrapperRef: wrapperRef,
  });

  useEffect(() => {
    setIsOpen(!!search);
  }, [search]);

  const hasSelected = selectedCount > 0;

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: "relative",
        width: fullwidth ? "100%" : "156px",
        borderRadius: "8px",
        boxSizing: "border-box",
      }}
    >
      <Box position={"relative"}>
        <CustomInput
          value={search}
          placeholder={placeholder}
          prefixIcon={<SearchIcon />}
          onChange={setSearch}
          clearButton
          onFocus={() => setIsOpen(true)}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            position: "absolute",
            top: "11px",
            right: "10px",
          }}
        >
          {hasSelected && showCounter && (
            <Box
              sx={{
                minWidth: "20px",
                width: "fit-content",
                height: "20px",
                backgroundColor: "#1B3967",
                color: "white",
                borderRadius: "20px",
                fontSize: "0.75rem",
                lineHeight: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "4px",
              }}
            >
              {selectedCount}
            </Box>
          )}
        </Box>
      </Box>

      {isOpen && (
        <Box
          ref={dropdownRef}
          sx={{
            position: "absolute",
            top: "Calc(100% + 8px)",
            left: 0,
            zIndex: 9999,
            width: fullwidth ? "100%" : "280px",
            paddingBottom: "6px",
          }}
        >
          <Box
            sx={{
              boxShadow: "0px 4px 15px 0px #00000040",
              borderRadius: "8px",
              paddingTop: "8px",
              width: fullwidth ? "100%" : "280px",
              backgroundColor: "white",
            }}
          >
            <Box
              sx={{
                maxHeight: listMaxHeight,
                "@media (max-height: 900px)": {
                  maxHeight: listMinHeight,
                },
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                marginRight: "4px",
              }}
              className="scrollable_container"
            >
              {children}
            </Box>

            <Stack direction="row" gap={1} padding="8px 16px">
              <CustomButton
                onClick={cancelHandler}
                variant="contained"
                textColor="#4C545B"
                fullWidth
                type="button"
                customColor="#F1F1F1"
              >
                Cancel
              </CustomButton>
              <CustomButton
                onClick={submitHandler}
                variant="contained"
                fullWidth
                type="button"
                customColor="#1C3C6C"
              >
                Apply
              </CustomButton>
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};
